import React, { useState, useEffect } from 'react';
import { serviceApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import './ServicesPage.css';

const COLORS = [
  '#534AB7','#E74C3C','#2ECC71','#F39C12',
  '#3498DB','#9B59B6','#E91E63','#00BCD4',
  '#FF5722','#607D8B','#16A085','#8E44AD',
];

const fmtDuration = (min) => {
  if (!min) return '—';
  if (min < 60) return `${min} λεπτά`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h}ω ${m}λ` : `${h} ώρ${h === 1 ? 'α' : 'ες'}`;
};

const fmtPrice = (price) =>
  price ? `${parseFloat(price).toFixed(2)} €` : '—';

const validate = (f) => {
  const errs = {};
  if (!f.name.trim())
    errs.name = '* Η ονομασία είναι υποχρεωτική';
  if (!f.duration || isNaN(f.duration) || Number(f.duration) < 5)
    errs.duration = '* Τουλάχιστον 5 λεπτά';
  if (f.price !== '' && f.price !== null && (isNaN(f.price) || Number(f.price) < 0))
    errs.price = '* Μη έγκυρη τιμή';
  return errs;
};

// ── ServiceModal ─────────────────────────────────────────────
function ServiceModal({ service, onSave, onDelete, onClose }) {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name:     service?.name          || '',
    duration: service?.duration      || 30,
    price:    service?.price != null ? service.price : '',
    color:    service?.color         || COLORS[0],
  });
  const [touched, setTouched]       = useState({});
  const [saving, setSaving]         = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [serverError, setServerError] = useState('');

  const errors  = validate(form);
  const hasErrs = Object.keys(errors).length > 0;

  const blur  = (field) => setTouched(t => ({ ...t, [field]: true }));
  const touch = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, duration: true, price: true });
    if (hasErrs) return;
    setSaving(true); setServerError('');
    try {
      await onSave({ ...form, id: service?.id });
    } catch (err) {
      const data = err.response?.data;
      setServerError(data?.errors?.[0]?.msg || data?.error || 'Σφάλμα αποθήκευσης. Δοκίμασε ξανά.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDel) { setConfirmDel(true); return; }
    await onDelete(service.id);
  };

  return (
    <div className="sv-overlay">
      <div className="sv-modal">
        <div className="sv-modal-header">
          <span className="sv-modal-title">
            {isEdit ? '✏️ Επεξεργασία υπηρεσίας' : '➕ Νέα υπηρεσία'}
          </span>
          <button className="sv-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="sv-modal-body">
          <div className={`sv-field ${touched.name && errors.name ? 'sv-field-error' : ''}`}>
            <label>Ονομασία <span className="sv-required">*</span></label>
            <input type="text" placeholder="π.χ. Κόψιμο, Βαφή, Χτένισμα"
              value={form.name} onChange={e => touch('name', e.target.value)}
              onBlur={() => blur('name')} autoFocus />
            {touched.name && errors.name && <span className="sv-field-msg">{errors.name}</span>}
          </div>

          <div className="sv-field-row">
            <div className={`sv-field ${touched.duration && errors.duration ? 'sv-field-error' : ''}`}>
              <label>Διάρκεια (λεπτά) <span className="sv-required">*</span></label>
              <input type="number" min="5" max="480" step="5" placeholder="30"
                value={form.duration} onChange={e => touch('duration', e.target.value)}
                onBlur={() => blur('duration')} />
              {touched.duration && errors.duration && <span className="sv-field-msg">{errors.duration}</span>}
            </div>
            <div className={`sv-field ${touched.price && errors.price ? 'sv-field-error' : ''}`}>
              <label>Τιμή (€)</label>
              <input type="number" min="0" step="0.50" placeholder="0.00"
                value={form.price} onChange={e => touch('price', e.target.value)}
                onBlur={() => blur('price')} />
              {touched.price && errors.price && <span className="sv-field-msg">{errors.price}</span>}
            </div>
          </div>

          <div className="sv-field">
            <label>Χρώμα</label>
            <div className="sv-color-row">
              {COLORS.map(c => (
                <button key={c} type="button"
                  className={'sv-color-dot' + (form.color === c ? ' active' : '')}
                  style={{ background: c }} onClick={() => touch('color', c)} />
              ))}
            </div>
          </div>

          <div className="sv-preview" style={{ borderLeftColor: form.color, background: form.color + '14' }}>
            <span className="sv-preview-name" style={{ color: form.color }}>
              {form.name || 'Προεπισκόπηση'}
            </span>
            <span className="sv-preview-meta">
              {fmtDuration(Number(form.duration) || 0)}
              {form.price !== '' && form.price !== null
                ? ` · ${parseFloat(form.price || 0).toFixed(2)} €` : ''}
            </span>
          </div>

          {serverError && <div className="sv-error">{serverError}</div>}

          <div className="sv-modal-footer">
            {isEdit && (
              <button type="button" className="sv-btn sv-btn-danger"
                onClick={handleDelete} style={{ marginRight: 'auto' }}>
                {confirmDel ? '⚠️ Επιβεβαίωση;' : '🗑️ Διαγραφή'}
              </button>
            )}
            <button type="button" className="sv-btn sv-btn-sec" onClick={onClose}>Ακύρωση</button>
            <button type="submit" className="sv-btn sv-btn-pri" disabled={saving}>
              {saving ? 'Αποθήκευση...' : isEdit ? '💾 Αποθήκευση' : '➕ Προσθήκη'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main ServicesPage ─────────────────────────────────────────
export default function ServicesPage() {
  const { openSidebar } = useAppLayout();
  const [services, setServices]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editService, setEditService] = useState(null);

  useEffect(() => {
    serviceApi.getAll()
      .then(r => setServices(r.data.services || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (data) => {
    const payload = {
      name:     data.name,
      duration: Number(data.duration),
      price:    data.price !== '' && data.price !== null ? Number(data.price) : null,
      color:    data.color,
    };
    if (data.id) {
      const res = await serviceApi.update(data.id, payload);
      setServices(prev => prev.map(s => s.id === data.id ? res.data.service : s));
    } else {
      const res = await serviceApi.create(payload);
      setServices(prev => [...prev, res.data.service]);
    }
    setShowModal(false); setEditService(null);
  };

  const handleDelete = async (id) => {
    await serviceApi.delete(id);
    setServices(prev => prev.filter(s => s.id !== id));
    setShowModal(false); setEditService(null);
  };

  const openEdit = (s) => { setEditService(s); setShowModal(true); };

  const totalServices = services.length;
  const avgDuration   = totalServices
    ? Math.round(services.reduce((a, s) => a + s.duration, 0) / totalServices) : 0;
  const withPrice = services.filter(s => s.price);
  const minPrice  = withPrice.length ? Math.min(...withPrice.map(s => parseFloat(s.price))) : null;
  const maxPrice  = withPrice.length ? Math.max(...withPrice.map(s => parseFloat(s.price))) : null;

  return (
    <div className="sv-page">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="sv-header">
        {/* Γραμμή 1: hamburger + τίτλος + button */}
        <div className="sv-header-top">
          <div className="sv-header-left">
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="sv-title">✂️ Υπηρεσίες</h1>
          </div>
          <button className="sv-add-btn"
            onClick={() => { setEditService(null); setShowModal(true); }}>
            + Νέα υπηρεσία
          </button>
        </div>

        {/* Γραμμή 2: stats (μόνο αν υπάρχουν υπηρεσίες) */}
        {totalServices > 0 && (
          <div className="sv-stats">
            <div className="sv-stat">
              <span className="sv-stat-val">{totalServices}</span>
              <span className="sv-stat-lbl">Υπηρεσίες</span>
            </div>
            <div className="sv-stat">
              <span className="sv-stat-val">{fmtDuration(avgDuration)}</span>
              <span className="sv-stat-lbl">Μέση διάρκεια</span>
            </div>
            {minPrice !== null && (
              <div className="sv-stat">
                <span className="sv-stat-val">
                  {minPrice === maxPrice
                    ? `${minPrice.toFixed(2)} €`
                    : `${minPrice.toFixed(2)} – ${maxPrice.toFixed(2)} €`}
                </span>
                <span className="sv-stat-lbl">Εύρος τιμών</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="sv-content">
        {loading && (
          <div className="sv-empty">
            <div className="sv-spinner" />
            <p>Φόρτωση...</p>
          </div>
        )}

        {!loading && services.length === 0 && (
          <div className="sv-empty">
            <div className="sv-empty-icon">✂️</div>
            <p className="sv-empty-title">Δεν υπάρχουν υπηρεσίες ακόμα</p>
            <p className="sv-empty-sub">Πρόσθεσε την πρώτη υπηρεσία σου</p>
            <button className="sv-add-btn" style={{ marginTop: 14 }}
              onClick={() => { setEditService(null); setShowModal(true); }}>
              + Νέα υπηρεσία
            </button>
          </div>
        )}

        {!loading && services.length > 0 && (
          <div className="sv-grid">
            {services.map(s => (
              <div key={s.id} className="sv-card" onClick={() => openEdit(s)}
                style={{ '--scolor': s.color }}>
                <div className="sv-card-bar" style={{ background: s.color }} />
                <div className="sv-card-body">
                  <div className="sv-card-icon" style={{ background: s.color + '20', color: s.color }}>✂</div>
                  <div className="sv-card-info">
                    <div className="sv-card-name">{s.name}</div>
                    <div className="sv-card-tags">
                      <span className="sv-tag sv-tag-dur">🕐 {fmtDuration(s.duration)}</span>
                      {s.price && <span className="sv-tag sv-tag-price">💶 {fmtPrice(s.price)}</span>}
                    </div>
                  </div>
                  <div className="sv-card-arrow">›</div>
                </div>
              </div>
            ))}

            <div className="sv-card sv-card-add"
              onClick={() => { setEditService(null); setShowModal(true); }}>
              <div className="sv-card-add-inner">
                <span className="sv-card-add-icon">+</span>
                <span className="sv-card-add-label">Νέα υπηρεσία</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <ServiceModal service={editService} onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => { setShowModal(false); setEditService(null); }} />
      )}
    </div>
  );
}